import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {isEmpty, isNil, isNull, omit, pick} from "lodash";
import apiUser from "apiUser";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import minutesToHours from "../../../utils/minutesToHours";
import Icon from "../../../componentsReusable/Icon";
import WorkStep from "./WorkStep";
import useAfterUpdateEffect from "../../../utils/useAfterUpdateEffect";
import SearchInput from "../../../componentsReusable/AppTable/SearchInput";
import {Alert} from "reactstrap";
import BodyForTimesSelect from "./BodyForTimesSelect";
import Breadcrumb from "../../Breadcrumb";
import appUserRoutes from "../../../appUserRoutes";
import {getByModuleId, MODULE_TIMES} from "../Modules";
import {TAB_MODULES} from "../../../views/UserViews/TecRmi/MainView";
import {useStateWithCallbackLazy} from "use-state-with-callback";
import {useTecRmi} from "../../../contexts/TecRmiContext";
import useDidMountEffect from "../../../utils/useDidMountEffect";
import WorkTimeMultiplier from "../WorkTimeMultiplier";
import TreeMenuNew from "../../../componentsReusable/TreeMenuNew/TreeMenuNew";

export const workStepName = (workStep) => {
  return [
    workStep.ItemMpText,
    workStep.KorText && `(${workStep.KorText})`,
    (workStep.QualColId > 0 ? `(${workStep.QualColText})` : '')
  ]
    .filter(v => !!v)
    .join(' ')
}

export const mapWorkStepToItem = (workStep, workTimeMultiplier) => {
  workStep.WorkTime = workStep.WorkTimeDefault * workTimeMultiplier
  workStep.ExclusiveWorkPositions = workStep.ExclusiveWorkPositions.map(exclusiveWorkPosition => {
    if(!exclusiveWorkPosition.WorkTimeDefault){
      exclusiveWorkPosition.WorkTimeDefault = exclusiveWorkPosition.WorkTime
    }
    exclusiveWorkPosition.WorkTime = exclusiveWorkPosition.WorkTimeDefault * workTimeMultiplier

    return exclusiveWorkPosition
  })

  return workStep
}

export const WorkStepLabel = (props) => {
  return (
    <span>
      <span className="text-secondary">{minutesToHours(props.workStep.WorkTime, false, 2)}</span>
      <span className="ml-2">{workStepName(props.workStep)}</span>
    </span>
  )
}

const Times = props => {
  const params = props.params
  const { getApiPrimaryParams, workTimeMultiplier, ident } = useTecRmi()
  const [crumbs, setCrumbs] = useState([])
  const [textSearch, setTextSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [bodyForTimes, setBodyForTimes] = useState(!isNil(params[0]) ? parseInt(params[0]) : null)
  const [times, setTimes] = useStateWithCallbackLazy([])

  useDidMountEffect(() => {
    fetchData()
  })

  useAfterUpdateEffect(() => {
    setTimes(times => times.map(time => {
      time.SubGroups = time.SubGroups.map(subGroup => {
        subGroup.ItemMps = subGroup.ItemMps.map(itemMp => {
          if(itemMp.workSteps){
            itemMp.workSteps = itemMp.workSteps
              .map(workStep => mapWorkStepToItem(workStep, workTimeMultiplier))
          }

          return itemMp
        })

        return subGroup
      })

      return time
    }))
  }, [workTimeMultiplier])

  useAfterUpdateEffect(() => {
    setCrumbs([])
    fetchData()
  }, [textSearch])

  useAfterUpdateEffect(() => {
    if(isNull(bodyForTimes)){
      return;
    }

    fetchData()
  }, [bodyForTimes])

  const fetchData = () => {
    if(isNil(bodyForTimes)){
      return;
    }
    setLoading(true)
    setTimes([])

    getApiFn()({
      ...getApiPrimaryParams(),
      showPaint: false,
      componentSubgroupId: props?.componentSubgroupId ?? null,
      paintBodyQualColId: bodyForTimes,
      bodyQualColId: bodyForTimes,
      searchText: textSearch,
    })
      .then(res => {
        setTimes(res.data, times => {
          if(params[2]){
            const subGroup = times.find(mainGroup => mainGroup.MainGroupId === parseInt(params[1])).SubGroups.find(subGroup => subGroup.SubGroupId === parseInt(params[2]))
            fetchSubGroupDetails(subGroup, subGroup.ItemMps.map(item => pick(item, ['KorId', 'ItemMpId'])), parseInt(params[1]))
          }
        })


      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getApiFn = () => {
    if(props.componentSubgroupId){
      return apiUser.tecRmi.times.componentList
    }

    return apiUser.tecRmi.times.workList
  }

  const handleFetchWorkStepTime = (mainGroupId, subGroupId, workStep) => {
    apiUser.tecRmi.times.workSteps({
      ...getApiPrimaryParams(),
      kindOfWorkTime: 0,
      bodyQualColId: bodyForTimes,
      korId: workStep.KorId,
      itemMpId: workStep.ItemMpId,
    })
      .then(res => {
        setTimes(times => times.map(time => {
          if(time.MainGroupId === mainGroupId){
            time.SubGroups = time.SubGroups.map(_subGroup => {
              if(_subGroup.SubGroupId === subGroupId){
                _subGroup.ItemMps = _subGroup.ItemMps.map(itemMp => {
                  if(itemMp.ItemMpId === workStep.ItemMpId && itemMp.KorId === workStep.KorId){
                    itemMp.workSteps = res.data
                  }

                  return itemMp
                })
              }

              return _subGroup
            })
          }

          return time
        }))
      })
  }

  const fetchSubGroupDetails = (subGroup, items = null, mainGroupId = null) => {
    let objParams = {
      ...getApiPrimaryParams(),
      kindOfWorkTime: 0,
      bodyQualCodId: bodyForTimes,
    }
    let apiFn = apiUser.tecRmi.times.workStepsForSubgroup

    if(subGroup){
      if(!subGroup.id){
        return
      }
      objParams.subGroupId = subGroup.id
    }else{
      apiFn = apiUser.tecRmi.times.workStepsMulti
      objParams.items = items ?? []
    }

    apiFn(objParams)
      .then(res => {
        setTimes(times => times.map(time => {
          if(time.MainGroupId === subGroup._parentMainGroupId || time.MainGroupId === mainGroupId){
            time.SubGroups = time.SubGroups.map(_subGroup => {
              if(_subGroup.SubGroupId === subGroup?._data?.SubGroupId || _subGroup.SubGroupId === subGroup.SubGroupId){
                _subGroup.ItemMps = _subGroup.ItemMps.map(itemMp => {
                  itemMp.workSteps = res.data
                    .filter(workStep => workStep.ItemMpId === itemMp.ItemMpId && workStep.KorId === itemMp.KorId)
                    .map(workStep => {
                      workStep.WorkTimeDefault = workStep.WorkTime
                      workStep.WorkTime = workStep.WorkTime * workTimeMultiplier

                      workStep.ExclusiveWorkPositions = workStep.ExclusiveWorkPositions.map(exclusiveWorkPosition => {
                        exclusiveWorkPosition.WorkTimeDefault = exclusiveWorkPosition.WorkTime
                        exclusiveWorkPosition.WorkTime = exclusiveWorkPosition.WorkTime * workTimeMultiplier

                        return exclusiveWorkPosition
                      })

                      return workStep
                    })

                  return itemMp
                })
              }

              return _subGroup
            })
          }

          return time
        }))
      })
  }

  const getPath = (params = []) => {
    if(props.disableRoutes){
      return null
    }

    if(!isNil(props.pathPrefix)){
      return props.pathPrefix + params.join('/')
    }

    return appUserRoutes.tecRmi.tabModule(ident, getByModuleId(MODULE_TIMES, 'uriName')) + params.join('/')
  }

  const getPathHome = () => {
    return props?.pathHome ?? appUserRoutes.tecRmi.tab(ident, TAB_MODULES)
  }

  const getMappedItems = () => times.map(time => ({
    _type: 'mainGroup',
    _data: omit(time, ['SubGroups']),
    id: time.MainGroupId,
    key: 'mainGroup' + time.MainGroupId,
    label: time.MainGroupName,
    path: getPath([bodyForTimes, time.MainGroupId]),
    items: time.SubGroups.map(subGroup => ({
      _type: 'subGroup',
      _parentMainGroupId: time.MainGroupId,
      _data: omit(subGroup, ['ItemMps']),
      id: subGroup.SubGroupId,
      key: 'subGroup' + subGroup.SubGroupId,
      onShowFn: fetchSubGroupDetails,
      label: subGroup.SubGroupName,
      path: getPath([bodyForTimes, time.MainGroupId, subGroup.SubGroupId]),
      items: subGroup.ItemMps.map(itemMp => {
        const itemMpId = `${itemMp.ItemMpId}-${itemMp.KorId}`
        let ret

        if(itemMp?.workSteps?.length === 1){
          const workStep = itemMp?.workSteps[0]
          const id = `${itemMpId}-${workStep.WorkId}`

          ret = {
            _type: 'workStep',
            _data: workStep,
            id,
            key: 'workStep' + workStep.WorkId,
            label: <WorkStepLabel workStep={workStep} />,
            path: getPath([bodyForTimes, time.MainGroupId, subGroup.SubGroupId, id]),
            breadcrumbPathName: workStepName(workStep),
            component: WorkStep,
            componentProps: {
              bodyForTimes,
              workStep,
            },
          }
        }else{
          const key = 'itemMp' + itemMp.ItemMpId + 'KorId' + itemMp.KorId
          const name = workStepName(itemMp)

          ret = {
            _type: 'itemMp',
            _parentSubGroupId: subGroup.SubGroupId,
            _data: itemMp,
            id: itemMpId,
            key,
            loading: isEmpty(itemMp?.workSteps),
            label: <span>
                <span className="text-secondary">{
                  isEmpty(itemMp?.workSteps)
                    ? <i className="fas fa-spin fa-spinner px-2" />
                    // ? <Icon className="fas fa-clock px-2" tooltip="Kliknij aby pobrać czas" onClick={e => {
                    //   e.preventDefault()
                    //   handleFetchWorkStepTime(time.MainGroupId, subGroup.SubGroupId, itemMp)
                    // }} />
                    : <Icon className="fas fa-info-circle px-1" tooltip="Możliwy wybór" />
                }</span>
                <span className="ml-2">{name}</span>
              </span>,
            path: getPath([bodyForTimes, time.MainGroupId, subGroup.SubGroupId, itemMpId]),
            breadcrumbPathName: name,
            items: itemMp?.workSteps?.map(workStep => {
              const name = workStepName(workStep)

              return {
                _type: 'workStep',
                _data: workStep,
                id: workStep.WorkId,
                key: 'workStep' + workStep.WorkId,
                label: <span>
                    <span className="text-secondary">{minutesToHours(workStep.WorkTime, false, 2)}</span>
                    <span className="ml-2">{name}</span>
                  </span>,
                path: getPath([bodyForTimes, time.MainGroupId, subGroup.SubGroupId, itemMpId, workStep.WorkId]),
                breadcrumbPathName: name,
                component: WorkStep,
                componentProps: {
                  bodyForTimes,
                  workStep,
                },
              }
            }) || [],
          }
        }

        return ret
      })
    }))
  }))

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <Breadcrumb
          crumbs={[
            {
              name: <i className="fas fa-chevron-circle-left" />,
              path: getPathHome(),
              hidden: true,
            },
            {
              name: getByModuleId(MODULE_TIMES, 'name'),
              path: getPath([bodyForTimes]),
              onClick: () => {
                setLoading(true)
                setCrumbs([])
                setTimeout(() => {
                  setLoading(false)
                }, 1)
              }
            },
            ...crumbs
          ]
          }
        />

        <div className="d-flex flex-row align-items-center ml-auto">
          <BodyForTimesSelect
            onChange={setBodyForTimes}
            value={bodyForTimes}
          />

          <WorkTimeMultiplier />

          <div className="justify-content-end ml-2">
            <SearchInput
              onInputChanged={setTextSearch}
            />
          </div>
        </div>
      </div>


      {!isNil(bodyForTimes) && <div className="mt-2">
        {loading
          ? <LoadingSpinner/>
          : (
            isEmpty(times)
              ? <Alert color={'warning'} className="text-center">Brak danych</Alert>
              : <TreeMenuNew
                actives={!props.disableRoutes ? props.params.slice(1) : undefined}
                onCrumbsChanged={setCrumbs}
                items={getMappedItems()}
              />
          )
        }
      </div>}
    </div>
  );
};

Times.defaultProps = {
  params: [],
  disableRoutes: false,
};

Times.propTypes = {
  pathHome: PropTypes.string,
  disableRoutes: PropTypes.bool,
  pathPrefix: PropTypes.string,
  componentSubgroupId: PropTypes.number,
  mainGroupId: PropTypes.number,
};

export default Times;